import { KnownDeliveryContainerDtoType } from '@groundfloor-platform/mgmt-service-g-sdk';
import { useState } from 'react';
import { LockerDetailsActions } from '../actions/locker-details.actions';
import {
  useQueryGetLocker,
  useQueryGetSpace,
  useQueryGetDeliveryContainerType,
  useQueryGetDeliveryContainerTransactionTypes
} from '../../lockers.queries';
import { useQueryGetHub, useQueryGetHubDetails } from '../../hubs.queries';
import { useLockerTimeline } from './use-locker-timeline.hook';

interface LockerDetailsPageState {
  currentAction: LockerDetailsActions | null;
}

const initialState: LockerDetailsPageState = {
  currentAction: null
};

export function useLockerDetails(deliveryContainerID: string) {
  const [state, setState] = useState(initialState);

  const containerType = useQueryGetDeliveryContainerType(deliveryContainerID);
  const transactionTypes = useQueryGetDeliveryContainerTransactionTypes(deliveryContainerID);
  const isLocker = containerType.data === KnownDeliveryContainerDtoType.Locker;
  const isSpace = containerType.data === KnownDeliveryContainerDtoType.Space;

  const locker = useQueryGetLocker(deliveryContainerID, isLocker);
  const space = useQueryGetSpace(deliveryContainerID, isSpace);

  const container = isLocker ? locker.data : space.data;
  const hub = useQueryGetHub(container?.hub.id);
  const hubDetails = useQueryGetHubDetails(container?.hub.id);
  const timeline = useLockerTimeline(deliveryContainerID, containerType.data);

  const tower =
    isLocker && locker.data && hubDetails.data
      ? (hubDetails.data.towers ?? []).find((t) => t.id === locker.data?.tower?.id)
      : undefined;

  const section =
    isSpace && hubDetails.data
      ? (hubDetails.data.sections ?? []).find((s) => s.spaces?.some((sp) => sp.id === deliveryContainerID))
      : undefined;

  function setAction(action: LockerDetailsActions | null) {
    setState((state) => ({ ...state, currentAction: action }));
  }
  async function endAction(nextAction?: LockerDetailsActions) {
    setAction(nextAction || null);
  }

  const isContainerLoading = containerType.isLoading || (isLocker ? locker.isLoading : space.isLoading);
  const containerError = containerType.error || (isLocker ? locker.error : space.error);

  return {
    container,
    locker: locker.data,
    space: space.data,
    containerType: containerType.data,
    hub,
    hubDetails,
    tower,
    section,
    timeline,
    transactionTypes,
    state,
    actions: { setAction, endAction },
    isContainerLoading,
    containerError
  } as const;
}
